import { Box, CircularProgress } from '@mui/material';
import { initGanttColumns } from 'components/projectOverview/gantt/ganttConfig/column/columnConfig';
import { drawFloatLayer } from 'components/projectOverview/gantt/ganttConfig/float';
import ganttStore from 'components/projectOverview/gantt/ganttConfig/ganttStore';
import { drawTargetEndLayer } from 'components/projectOverview/gantt/ganttConfig/targetEnd';
import { fetchTrades } from 'features/companies/store/companiesSlice';
import PlanningBoardTaskDrawer from 'features/drawers/components/PlanningBoardTaskDrawer';
import Gantt from 'features/gantt/components/Gantt';
import { addForecastedDatesLayer } from 'features/gantt/layers/addForecastedDatesLayer';
import { addWeekdaysLayer } from 'features/gantt/layers/addWeekdaysLayer';
import { fetchLocations } from 'features/locations/store/locationsSlice';
import { fetchProjects } from 'features/projects/store/projectsSlice';
import { fetchTasks, selectAllPlanningTasks } from 'features/tasks/store/tasksSlice';
import { taskGanttDataProcessor } from 'features/tasksGantt/utils/gantt/taskGanttDataProcessor';
import { formatGanttTaskToTask, formatTaskToGanttTask } from '@blackhyve/utilities/gantt';
import initMakers from 'features/tasksGantt/utils/gantt/taskGanttMarkers';
import { taskGanttRenderer } from 'features/tasksGantt/utils/gantt/taskGanttRenderer';
import { templates } from 'features/tasksGantt/utils/gantt/taskGanttTemplates';
import { zoomConfig } from 'features/tasksGantt/utils/gantt/zoomConfig';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const ContactGantt = ({ contactId, reloadGantt }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [ganttId, setGanttId] = useState(`contact-gantt-${contactId}`);
  const tasks = useSelector((state) => selectAllPlanningTasks(state)).map((task) =>
    formatTaskToGanttTask(task)
  );
  const taskIdSet = new Set(tasks.map((task) => task.id));

  useEffect(() => {
    setIsLoading(true);
    ganttStore.createGantt(ganttId);
    Promise.all([
      dispatch(fetchTasks()),
      dispatch(fetchLocations()),
      dispatch(fetchProjects()),
      dispatch(fetchTrades()),
    ]).then(() => {
      setIsLoading(false);
    });
  }, [contactId, dispatch, ganttId]);

  useEffect(() => {
    const newGanttId = `contact-gantt-${contactId}`;
    setGanttId(newGanttId);
  }, [contactId]);

  const getGanttColumn = () => {
    const columnEntities = initGanttColumns(ganttId);
    return [
      columnEntities.action,
      columnEntities.name,
      columnEntities.uuid,
      columnEntities.status,
      columnEntities.calendarDays,
      columnEntities.workingDays,
      columnEntities.startDate,
      columnEntities.endDate,
      columnEntities.forecastedStartDate,
      columnEntities.forecastedEndDate,
      columnEntities.actualStartDate,
      columnEntities.actualEndDate,
      columnEntities.effortHours,
      columnEntities.predecessor,
      columnEntities.successorColumn,
      columnEntities.crewSize,
      columnEntities.location,
      columnEntities.zone,
      columnEntities.area,
      columnEntities.companies,
      columnEntities.contacts,
      columnEntities.responsible,
      columnEntities.trades,
      columnEntities.constraintType,
      columnEntities.constraintDate,
      columnEntities.totalSlack,
      columnEntities.freeSlack,
      columnEntities.pace,
      columnEntities.earned_days,
      columnEntities.earned_planned_days,
      columnEntities.pr,
    ];
  };

  return (
    <>
      {isLoading || reloadGantt ? (
        <Box
          alignItems={'center'}
          display={'flex'}
          height={'100%'}
          justifyContent={'center'}
          width={'100%'}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Gantt
            data={{ tasks }}
            ganttId={ganttId}
            initDataProcessor={taskGanttDataProcessor}
            initMakers={initMakers}
            layers={[addWeekdaysLayer, drawTargetEndLayer, drawFloatLayer, addForecastedDatesLayer]}
            plugins={{ critical_path: true, grouping: true, tooltip: false, export_api: true }}
            templates={templates}
            typeRenders={{ task: taskGanttRenderer, project: taskGanttRenderer }}
            zoomConfig={zoomConfig}
            columns={[
              {
                name: 'action',
                label: '',
                width: 150,
                hide: false,
                canToggle: false,
                resize: true,
                sort: false,
                template: function (task) {
                  return ganttStore.getGantt(ganttId)?.getWBSCode(task);
                },
              },
              ...getGanttColumn(),
            ]}
            config={{
              fit_tasks: true,
              highlight_critical_path: false,
              sort: true,
              readonly: true,
              placeholder_task: false,
              order_branch: false,
              auto_types: false,
            }}
            events={{
              onTaskLoading: function (task) {
                const gantt = this;
                if (!taskIdSet.has(task.parent)) {
                  task.parent = gantt.config.root_id;
                }
                return true;
              },
              onGanttReady: function () {
                const el = document.querySelector('.gantt_grid');
                if (el) {
                  el.addEventListener('scroll', function () {
                    document.documentElement.style.setProperty(
                      '--gantt-frozen-column-scroll-left',
                      el.scrollLeft + 'px'
                    );
                  });
                }
              },
              onParse: function () {
                this._sort = { name: 'start_date', direction: 'asc' };
                this.sort('start_date', false);
              },
              onGridHeaderClick: function (name, event) {
                if (this._sort?.name === name && this._sort?.direction === 'desc') {
                  this._sort = null;
                  this.render();
                  return false;
                } else {
                  return true;
                }
              },
              onTaskRowClick: function () {
                return this.message({
                  type: 'warning',
                  text: 'You do not have permission to edit these tasks.',
                });
              },
            }}
          />
          <PlanningBoardTaskDrawer disableEdit ganttId={ganttId} />
        </>
      )}
    </>
  );
};
